import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 100px 0 0;
  position: relative;
  background: #0a1a33; /* Darker navy blue color matching the image */
  overflow: hidden;
`;

const HeroPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
`;

const HeroContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;

  @media (max-width: 992px) {
    padding: 120px 0 60px;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;
  padding-right: 20px;

  @media (max-width: 992px) {
    margin-bottom: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 0;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-block;
  background: linear-gradient(to right, rgba(0, 191, 179, 0.2), rgba(0, 59, 122, 0.2));
  color: var(--secondary-color);
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 191, 179, 0.3);
`;

const HeroHeading = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: #ffffff;
  margin-bottom: 24px;

  span {
    background: linear-gradient(to right, var(--secondary-color), var(--primary-light));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  @media (max-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubheading = styled(motion.p)`
  font-size: 1.1rem;
  color: #cbd5e1;
  margin-bottom: 40px;
  line-height: 1.7;
  max-width: 550px;

  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const HeroButtonGroup = styled(motion.div)`
  display: flex;
  justify-content: flex-start;

  @media (max-width: 992px) {
    justify-content: center;
  }

  @media (max-width: 576px) {
    width: 100%;
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    max-width: 260px;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  box-shadow: 0 10px 25px rgba(0, 80, 166, 0.3);
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(0, 80, 166, 0.4);
    border: 2px solid var(--secondary-light);
  }
  
  @media (max-width: 768px) {
    padding: 12px 28px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 0.9rem;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  color: white;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  background: rgba(0, 191, 179, 0.2);
  border: 2px solid var(--secondary-color);
  backdrop-filter: blur(5px);

  svg {
    margin-left: 8px;
    font-size: 1.2rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    background: rgba(0, 191, 179, 0.3);
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const HeroImage = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
  max-width: 50%;

  img {
    width: 100%;
    max-width: 550px;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
    object-fit: cover;
    aspect-ratio: 16/9;
  }

  @media (max-width: 1200px) {
    img {
      max-width: 500px;
    }
  }

  @media (max-width: 992px) {
    justify-content: center;
    width: 100%;
    max-width: 100%;
    margin-top: 20px;
    
    img {
      max-width: 80%;
    }
  }
  
  @media (max-width: 480px) {
    img {
      max-width: 95%;
      border-radius: 8px;
    }
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Custom arrow components for the slider
const SliderArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-50%) scale(1.05);
  }

  &:focus {
    outline: none;
  }

  &.prev-arrow {
    left: 30px;
  }

  &.next-arrow {
    right: 30px;
  }

  @media (max-width: 992px) {
    top: 40%;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    &.prev-arrow {
      left: 15px;
    }

    &.next-arrow {
      right: 15px;
    }
  }
`;

const SlideContainer = styled.div`
  display: flex !important;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 8%;
  max-width: 1800px;
  margin: 0 auto;
  gap: 60px;

  @media (max-width: 1200px) {
    gap: 40px;
  }

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    padding: 0 5%;
    gap: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 0 4%;
  }
`;

const SliderStatusBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
  gap: 10px;
  
  @media (max-width: 992px) {
    margin-top: 40px;
  }
  
  @media (max-width: 480px) {
    margin-top: 25px;
    gap: 8px;
  }
`;

const StatusDot = styled.div`
  width: ${props => props.active ? '30px' : '10px'};
  height: 10px;
  border-radius: 10px;
  background: ${props => props.active ? 'linear-gradient(to right, var(--secondary-color), var(--primary-light))' : 'rgba(255, 255, 255, 0.3)'};
  transition: all 0.3s ease;
  cursor: pointer;
  
  @media (max-width: 480px) {
    width: ${props => props.active ? '25px' : '8px'};
    height: 8px;
  }
`;

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    autoplay: true,
    autoplaySpeed: 5000,
  };

  const goToSlide = (index) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const slides = [
    {
      //badge: "Digital Marketing Agency",
      heading: "Elevate Your Brand with <span>Custom Web Solutions</span>",
      subheading: "From Design to Development and Digital Marketing, we're a digital marketing agency that takes a data-first approach to drive our marketing strategies.",
      buttonText: "Get Started",
      image: "https://images.unsplash.com/photo-1547658719-da2b51169166?ixlib=rb-1.2.1&auto=format&fit=crop&w=1336&q=80",
      alt: "Web Development"
    },
    {
      //badge: "UI/UX Design",
      heading: "Create <span>Stunning User Experiences</span> That Convert",
      subheading: "Our UI/UX design services focus on creating intuitive, engaging interfaces that delight users and drive business results through data-driven design decisions.",
      buttonText: "View UI/UX Services",
      image: "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1336&q=80",
      alt: "UI/UX Design"
    },
    {
      //badge: "SEO Optimization",
      heading: "Boost Your <span>Online Visibility</span> & Rankings",
      subheading: "Our comprehensive SEO strategies help your business rank higher in search results, drive targeted traffic, and increase conversions through proven optimization techniques.",
      buttonText: "Explore SEO Services",
      image: "https://images.unsplash.com/photo-1562577309-4932fdd64cd1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1336&q=80",
      alt: "SEO Optimization"
    }
  ];

  return (
    <HeroSection id="home">
      <HeroPattern />
      <HeroContainer>
        <Slider ref={sliderRef} {...sliderSettings}>
          {slides.map((slide, index) => (
            <div key={index}>
              <SlideContainer>
                <HeroContent as={motion.div} 
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {/* <HeroBadge 
                    variants={fadeInUp}
                    initial="hidden"
                    animate="visible"
                  >
                    {slide.badge}
                  </HeroBadge> */}
                  
                  <HeroHeading 
                    variants={fadeInUp}
                    dangerouslySetInnerHTML={{ __html: slide.heading }}
                  />
                  
                  <HeroSubheading 
                    variants={fadeInUp}
                  >
                    {slide.subheading}
                  </HeroSubheading>
                  
                  <HeroButtonGroup 
                    variants={fadeInUp}
                  >
                    <PrimaryButton 
                      to="contact"
                      spy={true} 
                      smooth={true} 
                      offset={-80} 
                      duration={500}
                    >
                      {slide.buttonText}
                    </PrimaryButton>
                  </HeroButtonGroup>
                </HeroContent>
                
                <HeroImage
                  as={motion.div}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7, delay: 0.3 }}
                >
                  <motion.img 
                    src={slide.image} 
                    alt={slide.alt}
                    whileHover={{ y: -10, boxShadow: "0 35px 65px rgba(0, 0, 0, 0.35)" }}
                    transition={{ duration: 0.5 }}
                  />
                </HeroImage>
              </SlideContainer>
            </div>
          ))}
        </Slider>

        {/* Navigation Arrows */}
        <SliderArrow className="prev-arrow" onClick={goToPrev}>
          <FaArrowLeft />
        </SliderArrow>
        <SliderArrow className="next-arrow" onClick={goToNext}>
          <FaArrowRight />
        </SliderArrow>

        {/* Status Bar */}
        <SliderStatusBar>
          {slides.map((_, index) => (
            <StatusDot 
              key={index} 
              active={currentSlide === index} 
              onClick={() => goToSlide(index)}
            />
          ))}
        </SliderStatusBar>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero;
